import { Component, effect, inject, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { Button } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { getMeetingHistoryFiltersFormObject } from './utils/meeting-history-search-form-object';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { MeetingService } from '../meeting.service';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { FileService } from '@/components/shared/file-list/file-list.service';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { MessageService } from 'primeng/api';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { getMeetingHistoryListDataTableStructure } from './utils/meeting-history-data-table-object';
import { DataTableAction, DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { Router } from '@angular/router';

@Component({
  selector: 'sm-meetings-history',
  imports: [PageHeaderComponent, FormBuilderComponent, Button, TranslateModule, CommonModule, SpinnerComponent, DataTableBuilderComponent],
  templateUrl: './meetings-history.component.html',
  styleUrl: './meetings-history.component.scss',
})
export class MeetingsHistoryComponent implements OnInit {
  meetingService = inject(MeetingService);
  meetingRepository = inject(MeetingRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  fileService = inject(FileService);
  router = inject(Router);

  breadcrumb = [
    {
      label: 'meeting.title',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.history.title',
      routerLink: ['/meetings-history'],
    },
  ];

  selectedTypeDossier: BehaviorSubject<SelectOption | undefined> = new BehaviorSubject<SelectOption | undefined>({
    name: 'Ppm',
    code: 'PPM',
  });
  selectedDossier: BehaviorSubject<SelectOption | undefined> = new BehaviorSubject<SelectOption | undefined>(undefined);
  selectedCom: BehaviorSubject<SelectOption | undefined> = new BehaviorSubject<SelectOption | undefined>(undefined);

  loading = false;
  noData = false;
  data: any[] = [];

  totalRecords = 0;
  serverQuery = createServerQuery({});

  isSearchDisabled$ = combineLatest([this.selectedTypeDossier]).pipe(
    map(([dossier]) => !dossier),
    startWith(true)
  );

  formObject = getMeetingHistoryFiltersFormObject({
    dossierTypeSelected: this.selectedTypeDossier,
    dossierSelected: this.selectedDossier,
    selectedCom: this.selectedCom,
    meetingService: this.meetingService,
  });

  rowActionsFn: (target: any) => DataTableRowActions = (target: any) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};

    additionalActions['meetingActions'] = [];

    const paramKey = this.selectedTypeDossier.value?.code === 'dac' ? 'dacId' : 'ppmId';
    additionalActions['meetingActions'].push({
      icon: 'pi pi-play-circle',
      label: 'meeting.actions.details',
      action: () => this.router.navigate(['/meetings-history', 'details', { id: target.id, [paramKey]: this.selectedDossier.value?.code }]),
    });
    return {
      additionalActions,
    };
  };

  dataTableObject: DynamicDataTable = getMeetingHistoryListDataTableStructure({
    serverQuery: this.serverQuery,
    rowActionsFn: this.rowActionsFn,
  });

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.serverQuery.search();
      this.confirm();
    });
  }

  ngOnInit() {
    this.formObject = getMeetingHistoryFiltersFormObject({
      dossierTypeSelected: this.selectedTypeDossier,
      dossierSelected: this.selectedDossier,
      selectedCom: this.selectedCom,
      meetingService: this.meetingService,
    });
  }

  confirm() {
    if (this.selectedTypeDossier.value && this.selectedDossier.value) {
      this.meetingService
        .getMeetingHistory(
          this.selectedTypeDossier.value.code as 'ppm' | 'dac',
          this.selectedDossier.value.code as number,
          this.selectedCom.value?.code as number,
          {
            search: this.serverQuery.search(),
            page: this.serverQuery.page(),
            size: this.serverQuery.pageSize(),
            sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : 'reunionDate,desc',
          }
        )
        .subscribe({
          next: data => {
            this.loading = false;
            this.data = data.content;
            this.noData = data.totalElements === 0;
            this.totalRecords = data.totalElements;
          },
          error: (error: any) => {
            this.loading = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: error.error.message || this.translate.instant('meeting.message.error.loading'),
            });
          },
        });
    }
  }
}
